import { default as axios, rest } from '@/api/axios';
import { gql } from '@/api/utils';

const orderFields = gql`
id
status
itemsCount
subtotal
shipping {
  type
  name
  description
  fee
}
hasShippableItems
eligibleForPriorityTrackedShipping
canDowngradeToRegularShipping
shippingFeeOptions {
  type
  name
  description
  fee
}
tax
processingFee
onlineProcessingReplacementTagFee
onlineProcessingFeeExcludingReplacementTag
discountAmount
discountAmountContext {
  discountAmount
  confirmationContext {
    success
  }
}
discount {
  code
  includeSaleItems
}
total
pets {
  id
}
jurisdiction {
  tagBackLeftZoneLine1
  tagBackLeftZoneLine2
}
city {
  baseUrl
  title
  taxCode
  taxRate
  processingFeeLabel
  processingFeeTooltipCopy
  replacementTagProcessingFeeLabel
  replacementTagProcessingFeeTooltipCopy
  name
  timezone
  safeAndHappyFund
  paymentScreen
  locale
  province
  country
  enableQrCodeTagBack
  customerService {
    phoneNumber
  }
  showWalletCardProducts
  showPetTrackerInStoreForPetOwner
}
items {
  id
  discount
  subtotal
  total
  customization
  quantity
  isCompleted
  addons {
    type
    title
    included
    retailPrice
    discountPrice
    savingAmount
  }
  parent {
    id
    product {
      id
      ... on DesignerTagProduct {
        style {
          id
          code
        }
      }
    }
  }
  product {
    id
    type
    name
    price
    description
    image {
      original
    }
    parcelShippingRequired
    ... on DesignerTagProduct {
      size
      color
      style {
        id
        code
      }
      municipalTag
      personalization {
        price
      }
      material {
        material
      }
      laserPrinterJig {
        shape
        material
      }
      keyChainPrice
      keyChainDiscountedPrice
      discountedPrice
    }
    ... on PetTagProduct {
      size
      municipalTag
      style {
        id
        code
      }
      discountedPrice
    }
    ... on SubscriptionProduct {
      productSubType
    }
    ... on CollarProduct {
      size
      color
      backImage {
        original
      }
      personalization {
        price
      }
      style {
        id
        code
        productGroups {
          id
        }
      }
      discountedPrice
    }
    ... on LeashProduct {
      color
      length
      backImage {
        original
      }
      style {
        id
        code
        productGroups {
          id
        }
      }
      discountedPrice
    }
    ... on PetEssentialsProduct {
      status
      color
      size
      image {
        original
      }
      style {
        id
        code
        productGroups {
          id
        }
      }
      discountedPrice
    }
    ... on AdditionalFeeProduct {
      subType
    }
    ... on LateFeeProduct {
      subType
    }
    ... on VoiceAndSightProduct {
      subType
    }
    ... on SubscriptionProduct {
      subscriptionInterval
      subscriptionIntervalCount
    }
  }
  group {
    id
    pet {
      id
      name
      idCode
      licenses {
        id
      }
    }
  }
  customTagOrderInfo {
    id
    name
    phone
  }
  children {
    id
    discount
    subtotal
    total
    orderItemBundle {
      id
    }
    group {
      pet {
        name
      }
    }    
    parent {
      id
      product {
        id
        ... on DesignerTagProduct {
          style {
            id
            code
          }
        }
      }
    }
    product {
      id
      name
      type
      price
      description
      image {
        original
      }
      ... on DesignerTagProduct {
        keyChainPrice
        keyChainDiscountedPrice
      }
      ... on PetEssentialsProduct {
        sku
      }
      ... on SubscriptionProduct {
        subscriptionInterval
        subscriptionIntervalCount
      }
    }
    keyChain
  }
  orderItemBundle {
    id
    badge {
      text
      color
      backgroundColor
    }
  }
}
groups {
  id
  walletCardConfigurations {
    optedIn
    completed
    url
    orderItemBundle {
      badge {
        text
        color
        backgroundColor
      }
    }
  }
  freePetTagContext {
    groupNotificationText
    containsFreePetTagWithReplacementFee
  }
  pet {
    id
    idCode
    name
    tags {
      ... on DesignerTagProduct {
        id
        municipalTag
      }
      ... on PetTagProduct {
        id
        municipalTag
      }
    }
    petTags {
      isValidInCurrentCity
      product {
        id
        name
        ... on DesignerTagProduct {
          size
        }
        ... on PetTagProduct {
          size
        }
      }
    }
    images {
      id
      original
    }
    useReplacementTagInLicensingFlow
  }
  items {
    id
    product {
    id
    type
    name
    price
    ... on DesignerTagProduct {
      municipalTag
    }
    }
  }
}
petOwner{
  id
  primaryPhone
  primaryNumberType
  phone
  cellPhone
  workPhone
  validatedPhone: phone(validate: true)
  validatedCellPhone: cellPhone(validate: true)
  validatedWorkPhone: workPhone(validate: true)
  addressDisplay
  pets{
    id
    name
    gender
    idCode
    tags {
      ... on PetTagProduct { id name }
      ... on DesignerTagProduct { id name }
    }
  }
  city {
    country
    customerService {
      phoneNumber
    }
  }
}
ableToCheckout
`;

const ORDER_UPDATED_EVENT = 'docupet:orderUpdated';

const dispatchOrderUpdatedEvent = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(ORDER_UPDATED_EVENT));
  }
};

export default {
  async getOrder(id) {
    try {
      const query = gql`
        query order($id: ID!) {
          order(id: $id) {
           ${orderFields}
          }
        }
      `;

      const variables = { id };
      const { order: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async addProductBundle(orderId, tagProductId, petId, personalization) {
    try {
      const query = gql`
        mutation AddProductBundleToCartMutation($orderId: ID!, $productBundleInputs: AddProductBundleInput!) {
          addProductBundleToCart(orderId: $orderId, productBundleInputs: $productBundleInputs) {
            ${orderFields}
          }
        }
      `;

      const variables = {
        orderId,
        productBundleInputs: {
          tagProductId,
          petId,
          personalization,
        },
      };

      const { addProductBundleToCart: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async updateProductBundle(orderId, orderItemBundleId, tagProductId, petId, personalization) {
    try {
      const query = gql`
        mutation UpdateProductBundleMutation($orderId: ID!, $orderItemBundleId: ID!, $productBundleInputs: AddProductBundleInput!) {
          updateProductBundle(orderId: $orderId, orderItemBundleId: $orderItemBundleId, productBundleInputs: $productBundleInputs) {
            ${orderFields}
          }
        }
      `;

      const variables = {
        orderId,
        orderItemBundleId,
        productBundleInputs: {
          tagProductId,
          petId,
          personalization,
        },
      };

      const { updateProductBundle: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async addItem(orderId, productDetails) {
    try {
      const query = gql`
        mutation AddItemMutation($orderId: ID!, $productDetails: [OrderProductInput!]) {
          addItem(orderId: $orderId, products: $productDetails) {
            ${orderFields}
          }
        }
      `;

      const variables = { orderId, productDetails: [productDetails] };
      const { addItem: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async addReplacementTagItem(orderId, productDetails) {
    try {
      const query = gql`
          mutation AddReplacementTagItemMutation($orderId: ID!, $productDetails: [OrderProductInput!]) {
              addReplacementTagForPetOwner(orderId: $orderId, products: $productDetails) {
                  ${orderFields}
              }
          }
      `;

      const variables = { orderId, productDetails: [productDetails] };
      const { addItem: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async addPersonalization(orderId, { personalization, itemId }) {
    try {
      const query = gql`
        mutation addPersonalization($orderId: ID!, $itemId: ID!, $personalization: PersonalizationInput!) {
          addPersonalization(orderId: $orderId, itemId: $itemId, personalization: $personalization) {
            ${orderFields}
          }
        }
      `;

      const variables = { orderId, itemId, personalization };
      const { addPersonalization: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async removeItem(orderId, itemId) {
    try {
      const query = gql`
        mutation removeItem($orderId: ID!, $itemId: ID!) {
          removeItem(orderId: $orderId, itemId: $itemId) {
            ${orderFields}
          }
        }
      `;

      const variables = { orderId, itemId };
      const { removeItem: results } = await axios.post(`/`, { query, variables });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async removeWalletCard(input) {
    try {
      const query = gql`
        mutation updateOrderForPet($input: UpdateOrderForPetInput!) {
          updateOrderForPet(input: $input) {
            ${orderFields}
          }
        }
      `;
      const variables = {
        input,
      };
      const { updateOrderForPet: results } = await axios.post(`/`, { query, variables });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async addItems(orderId, items) {
    try {
      let query = gql`
        mutation AddItemsMutation($orderId: ID!, $products: [OrderProductInput!]) {
          addItem(orderId: $orderId, products: $products) {
            id
          }
        }
      `;

      const variables = {
        orderId,
        products: items
          ?.filter((i) => i && i.productId)
          ?.map(({ productId, children, personalization, designExports, petId = null, keyChain }) => ({
            productId,
            petId,
            keyChain: !!keyChain,
            children: children?.map(({ productId }) => ({ productId }))?.filter((c) => c && c.productId),
            personalization: personalization ? { name: personalization.name, phone: personalization.phone } : null,
            designExports: designExports ? { tagFront: designExports.tagFront, laserFront: designExports.laserFront } : null,
          })),
      };
      const { addItem: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async orderNoTag(orderId, petId) {
    try {
      const query = gql`
        mutation OrderNoTagMutation($orderId: ID!, $petId: ID!) {
          orderNoTag(orderId: $orderId, petId: $petId) {
            id
          }
        }
      `;

      const variables = { orderId, petId };
      const { orderNoTag: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async assignItem(orderId, items) {
    try {
      let query = gql`
        mutation AssignItemMutation($orderId: ID!) {
        `;

      let count = 0;
      Object.entries(items).map(([itemId, item]) => {
        query =
          query +
          gql`
          item${count.toString()} : assignItem(
            orderId: $orderId, 
            itemId: ${itemId},
            petId: ${item.petId},
            ${
              item.personalization
                ? 'personalization: { name: "' + item.personalization.name + '", phone: "' + item.personalization.phone + '" }'
                : 'personalization: null'
            }
          ) {
            id
          }
        `;
        count += 1;
      });

      query = query + gql`}`;

      const variables = { orderId, items };
      const { assignItem: results } = await axios.post(`/`, { query, variables });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async applyDiscount(id, code) {
    try {
      const query = gql`
        mutation applyDiscount($id: ID! $code: String!){
          applyDiscount(order: $id, code: $code){
            ${orderFields}
          } 
        }
      `;

      const variables = { id, code };
      const { applyDiscount: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async removeDiscount(id) {
    try {
      const query = gql`
        mutation removeDiscount($id: ID!){
          removeDiscount(orderId: $id){
            ${orderFields}
          } 
        }
      `;

      const variables = { id };
      const { removeDiscount: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async removePersonalization(orderId, itemId) {
    try {
      const query = gql`
        mutation removePersonalization($orderId: ID!, $itemId: ID!){
          removePersonalization(orderId: $orderId, itemId: $itemId){
            ${orderFields}
          } 
        }
      `;

      const variables = { orderId, itemId };
      const { removePersonalization: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async uploadLaserCustomDesignFront(itemId, dataURL) {
    if (!dataURL) return Promise.reject();
    const formData = new FormData();
    formData.append('encodedContent', dataURL);
    try {
      return Promise.resolve(rest.post(`/api/custom-order/laser-custom-design-front?${itemId ? `itemId=${itemId}` : ''}`, formData));
    } catch (error) {
      return Promise.reject({
        message: error.message[0].message,
      });
    }
  },
  async uploadLaserCustomDesignFrontSvg(itemId, dataURL) {
    if (!dataURL) return Promise.reject();
    const formData = new FormData();
    formData.append('encodedContent', dataURL);
    try {
      return Promise.resolve(rest.post(`/api/custom-order/laser-custom-design-front-svg?${itemId ? `itemId=${itemId}` : ''}`, formData));
    } catch (error) {
      return Promise.reject({
        message: error.message[0].message,
      });
    }
  },
  async uploadCustomDesignFront(itemId, dataURL) {
    if (!dataURL) return Promise.reject();
    const formData = new FormData();
    formData.append('encodedContent', dataURL);
    try {
      return Promise.resolve(rest.post(`/api/custom-order/custom-design-front?${itemId ? `itemId=${itemId}` : ''}`, formData));
    } catch (error) {
      return Promise.reject({
        message: error.message[0].message,
      });
    }
  },
  async uploadWalletCardDesigns(orderItemId, frontDesign, backDesign) {
    if (!orderItemId || !frontDesign || !backDesign) return Promise.reject();
    const designs = {
      frontDesign,
      backDesign,
    };

    try {
      return Promise.resolve(rest.post(`/api/order-items/${orderItemId}/designs`, designs));
    } catch (error) {
      return Promise.reject({
        message: error.message[0].message,
      });
    }
  },
  async temporaryOrder(cityId, products, discountCode) {
    try {
      const query = gql`
        mutation ($cityId: ID!, $products: [OrderProductInput]!, $discountCode: String) {
          temporaryOrder(cityId: $cityId, products: $products, discountCode: $discountCode) {
            status
            city {
              baseUrl
              title
              logo {
                original
              }
              taxRate
              taxCode
              taxNumber
              name
              locale
              province
              country
              showCollarsLeashesProducts
              showPetTrackerInStoreForPetOwner
              showPetEssentialsProducts
              customerService {
                phoneNumber
              }
            }
            subtotal
            shipping {
              type
              name
              description
              fee
            }
             tax
             onlineProcessingFeeExcludingReplacementTag
             onlineProcessingReplacementTagFee
             discountAmount
            discount {
              code
              includeSaleItems
            }
            total
            itemsCount
            hasShippableItems
            eligibleForPriorityTrackedShipping
            canDowngradeToRegularShipping
            items {
              subtotal
              total
              customization
              customTagOrderInfo {
                name
                phone
              }
              product {
                id
                name
                type
                price
                image {
                  original
                }
                ... on DesignerTagProduct {
                  size
                  color
                  style {
                    id
                    code
                  }
                  municipalTag
                  personalization {
                    price
                  }
                  laserPrinterJig {
                    shape
                    material
                  }
                  keyChainPrice
                  keyChainDiscountedPrice
                  discountedPrice
                }
                ... on PetTagProduct {
                  size
                  municipalTag
                  style {
                    id
                    code
                  }
                  discountedPrice
                }
                ... on CollarProduct {
                  size
                  color
                  backImage {
                    original
                  }
                  personalization {
                    price
                  }
                  style {
                    id
                    code
                    productGroups {
                      id
                    }
                  }
                  discountedPrice
                }
                ... on LeashProduct {
                  color
                  length
                  backImage {
                    original
                  }
                  style {
                    id
                    code
                    productGroups {
                      id
                    }
                  }
                  discountedPrice
                }
                ... on PetEssentialsProduct {
                  status
                  color
                  size
                  image {
                    original
                  }
                  style {
                    id
                    code
                    productGroups {
                      id
                    }
                  }
                  discountedPrice
                }
                ... on AdditionalFeeProduct {
                  subType
                }
              }
              parent {
                total
                product {
                  id
                  ... on DesignerTagProduct {
                    style {
                      id
                      code
                    }
                  }
                }
              }
              children {
                discount
                subtotal
                total
                parent {
                  total
                  product {
                    id
                    ... on DesignerTagProduct {
                      style {
                        id
                        code
                      }
                    }
                  }
                  customTagOrderInfo {
                    name
                    phone
                  }
                }
                product {
                  id
                  name
                  type
                  price
                  description
                  image {
                    original
                  }
                  ... on DesignerTagProduct {
                    keyChainPrice
                    keyChainDiscountedPrice
                  }
                  ... on PetEssentialsProduct {
                    sku
                  }
                }
                keyChain
              }
              discount
              keyChain
            }
          }
        }
      `;
      const variables = { cityId, products, discountCode };
      const { temporaryOrder: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async cancelOrder(orderId) {
    try {
      const query = gql`
        mutation CancelOrderMutation($orderId: ID!) {
          cancelOrder(orderId: $orderId){
            ${orderFields}
          } 
        }
      `;

      const variables = { orderId };
      const { cancelOrder: results } = await axios.post(`/`, { query, variables });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async upgradeToPriorityShipping(orderId) {
    try {
      const query = gql`
        mutation UpgradeToPriorityTrackedShippingMutation($orderId: ID!) {
          addPriorityTrackedShipping(orderId: $orderId){
            ${orderFields}
          } 
        }
      `;

      const { addPriorityTrackedShipping: results } = await axios.post(`/`, { query, variables: { orderId } });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async downgradeToRegularShipping(orderId) {
    try {
      const query = gql`
        mutation DowngradeToRegularMailShippingMutation($orderId: ID!) {
          removePriorityTrackedShipping(orderId: $orderId){
            ${orderFields}
          } 
        }
      `;

      const { removePriorityTrackedShipping: results } = await axios.post(`/`, { query, variables: { orderId } });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async removeItemsByProduct(orderId, productId, quantity) {
    try {
      const query = gql`
        mutation removeItemsByProduct($orderId: ID!, $productId: ID!, $quantity: Float!) {
          removeItemsByProduct(orderId: $orderId, productId: $productId, quantity: $quantity) {
            ${orderFields}
          }
        }
      `;

      const variables = { orderId, productId, quantity: Number(quantity) };
      const { removeItemsByProduct: results } = await axios.post(`/`, { query, variables });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
};
